import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteTrip,
  listTrips,
  updateTrip,
  type Trip,
} from "../../api/trips";
import { useAuth } from "../../auth/AuthContext";
import { TripCard } from "./TripCard";

export function TripsSection({
  onScrollToForm,
}: {
  onScrollToForm?: () => void;
}) {
  const { user } = useAuth();
  const { t } = useTranslation("trips");
  const [trips, setTrips] = useState<Trip[] | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Trip | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<Trip | null>(null);

  useEffect(() => {
    if (!user) return;
    listTrips({ includeArchived: showArchived })
      .then(setTrips)
      .catch(() => setError(t("home.loadFailed")));
  }, [showArchived, user]);

  if (!user) return null;

  const liveTrips = trips?.filter((t) => !t.archived) ?? [];
  const archivedTrips = trips?.filter((t) => t.archived) ?? [];
  const visibleTrips = showArchived ? archivedTrips : liveTrips;

  const handleArchiveConfirm = async () => {
    if (!confirmArchive) return;
    try {
      await updateTrip(confirmArchive.id, { archived: true });
      setTrips((prev) =>
        prev ? prev.filter((t) => t.id !== confirmArchive.id) : null
      );
      setConfirmArchive(null);
    } catch {
      setError(t("trip.archiveFailed"));
    }
  };

  const handleRestore = async (trip: Trip) => {
    try {
      await updateTrip(trip.id, { archived: false });
      setTrips((prev) =>
        prev ? prev.filter((t) => t.id !== trip.id) : null
      );
    } catch {
      setError(t("trip.unarchiveFailed"));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      await deleteTrip(confirmDelete.id);
      setTrips((prev) =>
        prev ? prev.filter((t) => t.id !== confirmDelete.id) : null
      );
      setConfirmDelete(null);
    } catch {
      setError(t("trip.deleteFailed"));
    }
  };

  return (
    <section className="home-trips" data-testid="home-trips">
      <div className="home-trips__header">
        <div>
          <p className="home-trips__eyebrow">{t("home.yourTrips")}</p>
          <h2 className="home-trips__headline">{t("home.pickUp")}</h2>
        </div>
        <button
          type="button"
          className={
            "home-trips__archive-toggle" + (showArchived ? " is-active" : "")
          }
          data-testid="archive-toggle"
          onClick={() => setShowArchived((v) => !v)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M2 4h12v1H2V4zm1 2v7a1 1 0 001 1h8a1 1 0 001-1V6H3zm4 2h2v4H7V8z"
              fill="currentColor"
            />
          </svg>
          {showArchived ? t("home.liveTrips") : t("home.archive")}
        </button>
      </div>

      {error && (
        <p className="home-trips__error" role="alert">
          {error}
        </p>
      )}

      {trips === null ? (
        <div className="home-trips__loading">Loading…</div>
      ) : (
        <div className="home-trips__grid">
          {visibleTrips.map((trip) => (
            <TripCard
              key={trip.id}
              trip={trip}
              currentUserId={user?.id}
              onArchive={setConfirmArchive}
              onDelete={(t) => setConfirmDelete(t)}
              onRestore={handleRestore}
              showDelete={showArchived}
            />
          ))}
          {!showArchived && (
            <div
              className="trip-card trip-card--new"
              data-testid="new-trip-tile"
              onClick={onScrollToForm}
            >
              <div className="trip-card__new-inner">
                <span className="trip-card__new-icon">+</span>
                <span>{t("home.newTrip")}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {confirmArchive && (
        <div
          className="home-trips__confirm-overlay"
          data-testid="archive-confirm-dialog"
        >
          <div className="home-trips__confirm-dialog">
            <p>
              {t("trip.archiveConfirm", { title: confirmArchive.title })}
            </p>
            <div className="home-trips__confirm-actions">
              <button
                type="button"
                onClick={() => setConfirmArchive(null)}
              >
                {t("trip.archiveCancel")}
              </button>
              <button
                type="button"
                className="home-trips__confirm-archive"
                onClick={handleArchiveConfirm}
              >
                {t("trip.archiveButton")}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div
          className="home-trips__confirm-overlay"
          data-testid="delete-confirm-dialog"
        >
          <div className="home-trips__confirm-dialog">
            <p>
              {t("trip.deleteConfirm", { title: confirmDelete.title })}
            </p>
            <div className="home-trips__confirm-actions">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
              >
                {t("trip.deleteCancel")}
              </button>
              <button
                type="button"
                className="home-trips__confirm-delete"
                onClick={handleDeleteConfirm}
              >
                {t("trip.deleteButton")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
